'use client';

import { ChangeEvent, useContext, useState } from 'react';
import CustomConfigs from '~/constants/customConfigs';
import StaticContext from '~/context/common/StaticContext';
import BrowserCookies from '~/utilities/BrowserCookies';
import { getBranchCookieName } from '~/utilities/featureBranch';
import FormField from '../FormField';
import {
  Container,
  Heading,
  Input,
  SmallSecondaryButton,
  Text,
  TextStrong
} from '../PassengerTools.styles';
import { copyToClipboard } from '../utils/createTestUser';
import { findInventoryVehicles } from '../utils/findInventoryType';

const BRANCH_COOKIE_PREFIX = 'carvana-merch-merchui-';
const BRANCH_REGEX = /reo-[0-9a-z-]*/;

const SHARE_PAGES = [
  { label: 'Discover', path: '/discover' },
  { label: 'Home', path: '/' },
  { label: 'SEM', path: '/shop' },
  { label: 'SRP', path: '/cars' }
];

export default function ShareLink() {
  const { kubernetesAppName } = useContext(StaticContext);

  const [branchInput, setBranchInput] = useState(() => {
    const cookieValue =
      BrowserCookies.get(getBranchCookieName(kubernetesAppName)) ?? '';

    return cookieValue.startsWith(BRANCH_COOKIE_PREFIX)
      ? cookieValue.slice(BRANCH_COOKIE_PREFIX.length)
      : '';
  });
  const [copiedPath, setCopiedPath] = useState<string | null>(null);
  const [isFetchingVehicle, setIsFetchingVehicle] = useState(false);
  const [vdpVehicleId, setVdpVehicleId] = useState<number | null>(null);
  const [vehicleError, setVehicleError] = useState('');

  const parsedBranch = branchInput.toLowerCase().match(BRANCH_REGEX)?.[0] ?? '';
  const isValid = parsedBranch.length > 0;
  const showError = branchInput.length > 0 && !isValid;

  const handleCopy = (path: string) => {
    const url = `${window.location.origin}${path}?${CustomConfigs.FeatureBranch}=${parsedBranch}`;
    copyToClipboard(url);
    setCopiedPath(path);
    setTimeout(() => setCopiedPath(null), 2000);
  };

  const handleFetchVehicle = async () => {
    setIsFetchingVehicle(true);
    setVehicleError('');
    try {
      const result = await findInventoryVehicles({ inventoryType: 'WIP' });
      const firstVehicle = result.vehicles[0];
      if (firstVehicle) {
        setVdpVehicleId(firstVehicle.vehicleId);
      } else {
        setVehicleError('No WIP vehicles found.');
      }
    } catch {
      setVehicleError('Failed to fetch a vehicle ID.');
    } finally {
      setIsFetchingVehicle(false);
    }
  };

  const vdpPath = vdpVehicleId ? `/vehicle/${vdpVehicleId}` : null;
  const vdpUrl =
    isValid && vdpPath
      ? `${window.location.origin}${vdpPath}?${CustomConfigs.FeatureBranch}=${parsedBranch}`
      : null;

  return (
    <Container>
      <Heading>Generate Share Link</Heading>
      <FormField label="Branch Name" htmlFor="share-branch-name">
        <Input
          autoCorrect="off"
          id="share-branch-name"
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setBranchInput(e.target.value)
          }
          placeholder="reo-12345"
          type="text"
          value={branchInput}
        />
      </FormField>
      {showError && (
        <Text className="text-red-400">
          Could not parse a branch name — must match reo-12345 format.
        </Text>
      )}
      <div className="flex flex-col gap-md">
        {SHARE_PAGES.map(({ label, path }) => {
          const url = isValid
            ? `${window.location.origin}${path}?${CustomConfigs.FeatureBranch}=${parsedBranch}`
            : null;

          return (
            <div key={path} className="flex items-center gap-sm">
              <TextStrong className="w-[80px] shrink-0">{label}</TextStrong>
              {url ? (
                <>
                  <a
                    className="flex-1 truncate font-mono text-blue-400 underline t-body-xs"
                    data-analytics-id={null}
                    data-testid={`share-link-${label.toLowerCase()}`}
                    href={url}
                    rel="noreferrer"
                    target="_blank"
                  >
                    {url}
                  </a>
                  <SmallSecondaryButton
                    data-analytics-id={null}
                    data-testid={`copy-share-link-${label.toLowerCase()}`}
                    onClick={() => handleCopy(path)}
                  >
                    {copiedPath === path ? 'Copied!' : 'Copy'}
                  </SmallSecondaryButton>
                </>
              ) : (
                <Text className="flex-1 opacity-40">—</Text>
              )}
            </div>
          );
        })}

        {/* VDP — requires a real vehicle ID fetched on demand */}
        <div className="flex items-center gap-sm">
          <TextStrong className="w-[80px] shrink-0">VDP</TextStrong>
          {vdpUrl ? (
            <>
              <a
                className="flex-1 truncate font-mono text-blue-400 underline t-body-xs"
                data-analytics-id={null}
                data-testid="share-link-vdp"
                href={vdpUrl}
                rel="noreferrer"
                target="_blank"
              >
                {vdpUrl}
              </a>
              <SmallSecondaryButton
                data-analytics-id={null}
                data-testid="copy-share-link-vdp"
                onClick={() => handleCopy(vdpPath!)}
              >
                {copiedPath === vdpPath ? 'Copied!' : 'Copy'}
              </SmallSecondaryButton>
            </>
          ) : (
            <>
              <Text className="flex-1 opacity-40">
                {vdpVehicleId ? '—' : 'Need a vehicle ID'}
              </Text>
              <SmallSecondaryButton
                data-analytics-id={null}
                data-testid="fetch-vdp-vehicle-id"
                disabled={isFetchingVehicle}
                onClick={handleFetchVehicle}
              >
                {isFetchingVehicle ? 'Fetching…' : 'Fetch ID'}
              </SmallSecondaryButton>
            </>
          )}
        </div>
        {vehicleError && <Text className="text-red-400">{vehicleError}</Text>}
      </div>
    </Container>
  );
}
